<script lang="ts">
import { onMount } from 'svelte';

let {
    placeholder = '',
    name = '',
    role = 'listbox',
    options = [],
    selected = $bindable(''),
    onchange,
    ariaLabel = '',
} = $props();

// Derive a sensible aria-label if not explicitly provided
let computedAriaLabel = $derived(ariaLabel || `Select ${placeholder.toLowerCase()}`);

let selectedValue: HTMLElement | undefined = $state();
let selectBtn: HTMLElement | undefined = $state();

let selectedValueText = $derived(selectedValue?.textContent);

let buttonActive = $state(false);

// add click event to select button
const selectButtonClick = () => {
    if (!selectBtn) return;
    // add/remove active class on the container element
    buttonActive = !buttonActive;
    // update the aria-expanded attribute based on the current state
    selectBtn.setAttribute(
        'aria-expanded',
        selectBtn.getAttribute('aria-expanded') === 'true' ? 'false' : 'true',
    );

    selectBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

onMount(() => {
    document.addEventListener('click', (e) => {
        const target = e.target as Node | null;
        if (buttonActive && selectBtn && !selectBtn.contains(target)) {
            buttonActive = false;
            selectBtn.setAttribute('aria-expanded', 'false');
        }
    });
});

$effect(() => {
    if (!selectedValue) return;
    const sv = selectedValue; // Capture for type narrowing
    if (sv.textContent && selected) {
        if (options[0]?.id) {
            options.forEach((item: { id: string; name: string }) => {
                if (item.id.toLowerCase() === selected.toLowerCase()) {
                    sv.textContent = item.name;
                }
            });
        } else {
            options.forEach((item: string) => {
                if (item.toLowerCase() === selected.toLowerCase()) {
                    sv.textContent = item;
                }
            });
        }
        buttonActive = false;
    } else if (sv.textContent) {
        sv.textContent = placeholder;
    }
});
//selectedValue.textContent = options.find((option) => option.id === value).name;
</script>

<div class="custom-select" class:active={buttonActive}>
    <button
        class="select-button"
        bind:this={selectBtn}
        onclick={selectButtonClick}
        role="combobox"
        aria-label={computedAriaLabel}
        aria-haspopup="listbox"
        aria-expanded="false"
        aria-controls="select-dropdown"
    >
        <span class="selected-value" bind:this={selectedValue}>{placeholder}</span>
    </button>
    <ul class="select-dropdown" {role} id="select-dropdown">
        <li>
            <input
                type="radio"
                bind:group={selected}
                onchange={onchange}
                value=""
                id="{name}viewAll"
                {name}
            />
            <label for="{name}viewAll">View All</label>
        </li>
        {#if options[0].id}
            {#each options as option (option.id)}
                <li role="option" aria-selected={selectedValueText?.trim() === option.name.trim()}>
                    <input
                        type="radio"
                        onchange={onchange}
                        bind:group={selected}
                        value={option.id}
                        id={option.id}
                        {name}
                    />
                    <label for={option.id}>{option.name}</label>
                </li>
            {/each}
        {:else}
            {#each options as option (option)}
                <li role="option" aria-selected={selectedValueText?.trim() === option.trim()}>
                    <input
                        type="radio"
                        bind:group={selected}
                        onchange={onchange}
                        value={option.toLowerCase()}
                        id={option.toLowerCase()}
                        {name}
                    />
                    <label for={option.toLowerCase()}>{option}</label>
                </li>
            {/each}
        {/if}
    </ul>
</div>

<style lang="scss">
    @use '/src/scss/global-constants/fonts';
    @use 'sass-mq/mq' as *;

    .custom-select {
        color: var(--colour-old-silver);
        font-family: fonts.$fonts-secondary;
        font-size: 1.4rem;
        font-weight: fonts.$fonts-weight--normal;
        line-height: 1.6;
        max-width: 100%;
        position: relative;

        @include mq($from: tablet) {
            width: 21rem;
        }
    }

    .select-button {
        width: 100%;
        background-color: #fff;
        border: 1px solid var(--colour-old-silver);
        border-width: 0 0 1px;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        color: var(--colour-lucky-grey);
        scroll-margin-top: 6.2rem;

        @include mq($from: tablet) {
            scroll-margin-top: 7.3rem;
        }

        &:after {
            background: no-repeat url(/src/stories/assets/arrow-down.svg) right center;
            content: ' ';
            width: 1.4rem;
            height: 1.5rem;
            transition: transform var(--timing-duration-short) ease;
        }

        &:is(:global([aria-expanded='true'])) {
            &:after {
                transform: rotate(180deg);
            }
        }
    }

    .selected-value {
        text-align: left;
    }

    .select-dropdown {
        background-color: var(--colour-white);
        border-radius: 0 0 0.6rem 0.6rem;
        border: 1px solid var(--colour-orochimaru);
        box-shadow: rgba(102, 102, 102, 0.25) 0.4rem 0.4rem 0.4rem 0;
        line-height: 1.2;
        list-style: none;
        margin-top: -0.1rem;
        max-height: 80svh;
        opacity: 0;
        overflow-y: auto;
        padding: 1.2rem 1.7rem;
        position: absolute;
        top: 100%;
        transform-origin: top center;
        transform: scaleY(0);
        transition:
            transform var(--timing-duration-long) ease,
            opacity var(--timing-duration-long) ease,
            visibility var(--timing-duration-long) ease;
        visibility: hidden;
        width: 100%;
        z-index: 1;

        .active & {
            opacity: 1;
            transform: scaleY(1);
            visibility: visible;
        }

        li {
            align-items: center;
            cursor: pointer;
            display: flex;
            position: relative;

            label {
                align-items: center;
                cursor: pointer;
                display: flex;
                padding: 0.9rem 0;
                width: 100%;
            }
        }

        li:hover,
        input:checked ~ label {
            color: var(--colour-black);
        }

        input:focus ~ label {
            color: var(--colour-black);
        }

        input[type='radio'] {
            position: absolute;
            left: 0;
            opacity: 0;
        }
    }
</style>
